import { Link } from "react-router-dom";
import { ArrowRight, Check, Map, FileText, Building2, ShieldCheck, Clock, PiggyBank, Zap } from "lucide-react";
import { Eyebrow, Reveal, MaskLineView, ArrowCta, Faq } from "../components/ui-bits";
import JsonLd from "../components/JsonLd";
import { useLang } from "../i18n/LangContext";

const SITE = "https://verdecapital.eu";

const COPY = {
  en: {
    eyebrow: "Development finance · Portugal",
    h1a: "Development finance.",
    h1b: "Without the bank bureaucracy.",
    heroCopy: "Short-term, senior-secured financing of €1–5m for experienced real estate developers in Portugal — from land acquisition and licensing to construction readiness, before conventional bank financing is available.",
    answerH: "What is development finance in Portugal?",
    answerP: "Development finance is short-term, secured lending that funds a project through the stages a conventional bank will not usually finance: buying land, obtaining municipal licensing, and reaching the pre-sales that make the project bankable. In Portugal, banks typically enter at the construction stage — development finance carries the project until then, secured by a first-ranking mortgage, and is repaid from bank refinancing, sale or pre-sales.",
    forYouH: "Built for experienced developers",
    forYou: [
      "Acquiring land with development potential",
      "Funding licensing, design and municipal fees",
      "Bridging to pre-sales and construction lending",
      "Residential projects across Portugal",
    ],
    offerH: "What Verde Capital provides",
    offer: [
      { icon: PiggyBank, t: "€1–5m facilities", d: "Short-term senior-secured development loans for experienced residential developers." },
      { icon: Clock, t: "6–24 month terms", d: "Defined repayment routes: bank refinancing, sale or pre-sales." },
      { icon: Zap, t: "Indicative terms in 24 hours", d: "Describe the project once; Verde, our AI underwriter, builds the case." },
      { icon: ShieldCheck, t: "First-ranking mortgage", d: "Security over the land or project — with Verde investing its own capital alongside." },
    ],
    howH: "How it works",
    how: [
      { n: "1", t: "Describe your project", d: "Through the online application — or attach plans and appraisals." },
      { n: "2", t: "Verde builds your case", d: "AI-assisted underwriting with an internal credit team and local expert review." },
      { n: "3", t: "Indicative terms in 24 hours", d: "Then due diligence, security and funding — and a clear path to your bank refinancing." },
    ],
    vsH: "How it compares with bank financing",
    vsTable: { head: ["", "Verde development finance", "Conventional bank financing"], rows: [
      ["Project stage", "Land acquisition, licensing, pre-construction", "Construction and completion, once milestones exist"],
      ["Decision speed", "Indicative terms within 24 hours", "Committee cycles after a full dossier"],
      ["Typical size", "€1–5m", "Larger facilities at construction stage"],
      ["Term", "6–24 months", "Construction period plus sales"],
      ["Security", "First-ranking mortgage, Verde co-invests", "Mortgage plus assignments and pre-sale coverage"],
    ] },
    guidesEyebrow: "Keep learning",
    guides: [
      { slug: "development-finance-portugal-guide", t: "The Complete Guide to Real Estate Development Finance in Portugal", d: "Stages, security, underwriting, exits and risks — the full reference." },
      { slug: "finance-land-before-planning", t: "How to finance land before planning permission", d: "The realistic routes for acquisition-stage funding." },
      { slug: "bridge-loans-vs-bank-construction-loans", t: "Bridge loans vs bank construction loans", d: "What each instrument does, and where each belongs." },
      { slug: "why-banks-finance-later", t: "Why Portuguese banks finance later", d: "The milestones banks wait for — and what fills the gap." },
    ],
    faqH: "Questions developers ask",
    faqs: [
      { q: "What is development finance in Portugal?", a: "Short-term, senior-secured lending that funds a project through acquisition, licensing and pre-sales — the stages before conventional bank construction lending — repaid from bank refinancing, sale or pre-sales." },
      { q: "What can be financed?", a: "Land acquisition, licensing and design costs, municipal fees and the carry to pre-sales — typically €1–5m per facility for experienced residential developers." },
      { q: "How fast is the answer?", a: "Indicative terms within 24 hours of describing the project; funding follows underwriting, valuation and due diligence." },
      { q: "What security is required?", a: "A first-ranking mortgage over the land or project, with the developer's equity invested and Verde Capital co-investing its own capital." },
      { q: "How is it repaid?", a: "Through the next capital event: construction-stage bank refinancing, sale of the completed or part-completed asset, or pre-sale programmes." },
      { q: "Who can apply?", a: "Experienced developers with Portuguese residential projects and their own equity in the deal. Brokers and advisers can also introduce deals on behalf of developers." },
    ],
    ctaH: "Start with your project.",
    ctaCopy: "Describe it below. Verde asks what's missing and builds your case — indicative terms within 24 hours.",
    ctaBtn: "Get indicative terms",
    alt: "Development site under a crane on the Portuguese coast",
  },
  pt: {
    eyebrow: "Financiamento ao desenvolvimento · Portugal",
    h1a: "Financiamento ao desenvolvimento.",
    h1b: "Sem a burocracia bancária.",
    heroCopy: "Financiamento de curto prazo, com garantia sénior, de €1–5m para promotores imobiliários experientes em Portugal — da aquisição do terreno e do licenciamento à prontidão para construção, antes do crédito bancário convencional estar disponível.",
    answerH: "O que é o financiamento ao desenvolvimento em Portugal?",
    answerP: "O financiamento ao desenvolvimento é crédito de curto prazo, com garantia, que financia um projeto nas fases que um banco convencional normalmente não financia: a compra do terreno, o licenciamento municipal e as pré-vendas que tornam o projeto bancável. Em Portugal, os bancos normalmente entram na fase de construção — o financiamento ao desenvolvimento suporta o projeto até lá, com garantia de hipoteca de primeiro grau, e é reembolsado por refinanciamento bancário, venda ou pré-vendas.",
    forYouH: "Criado para promotores experientes",
    forYou: [
      "Adquirir terrenos com potencial de desenvolvimento",
      "Financiar licenciamento, projeto e taxas municipais",
      "Fazer a ponte até às pré-vendas e ao crédito à construção",
      "Projetos residenciais em todo o Portugal",
    ],
    offerH: "O que a Verde Capital fornece",
    offer: [
      { icon: PiggyBank, t: "Operações de €1–5m", d: "Empréstimos ao desenvolvimento de curto prazo, com garantia sénior, para promotores residenciais experientes." },
      { icon: Clock, t: "Prazos de 6–24 meses", d: "Vias de reembolso definidas: refinanciamento bancário, venda ou pré-vendas." },
      { icon: Zap, t: "Condições indicativas em 24 horas", d: "Descreva o projeto uma vez; a Verde, a nossa subscritora de IA, constrói o processo." },
      { icon: ShieldCheck, t: "Hipoteca de primeiro grau", d: "Garantia sobre o terreno ou o projeto — com a Verde a investir o seu próprio capital." },
    ],
    howH: "Como funciona",
    how: [
      { n: "1", t: "Descreva o seu projeto", d: "Na candidatura online — com opção de anexar planos e avaliações." },
      { n: "2", t: "A Verde constrói o processo", d: "Subscrição assistida por IA com equipa de crédito interna e revisão de especialistas locais." },
      { n: "3", t: "Condições indicativas em 24 horas", d: "Depois due diligence, garantias e financiamento — e um caminho claro para o refinanciamento bancário." },
    ],
    vsH: "Comparação com o financiamento bancário",
    vsTable: { head: ["", "Financiamento Verde", "Crédito bancário convencional"], rows: [
      ["Fase do projeto", "Aquisição, licenciamento, pré-construção", "Construção e conclusão, quando os marcos existem"],
      ["Rapidez da decisão", "Condições indicativas em 24 horas", "Ciclos de comité após dossier completo"],
      ["Dimensão típica", "€1–5m", "Operações maiores na fase de construção"],
      ["Prazo", "6–24 meses", "Período de construção mais vendas"],
      ["Garantia", "Hipoteca de primeiro grau, com coinvestimento Verde", "Hipoteca + cessões e cobertura de pré-vendas"],
    ] },
    guidesEyebrow: "Continue a aprender",
    guides: [
      { slug: "development-finance-portugal-guide", t: "Guia Completo do Financiamento Imobiliário em Portugal", d: "Fases, garantias, subscrição, saídas e riscos — a referência completa." },
      { slug: "finance-land-before-planning", t: "Como financiar um terreno antes do licenciamento", d: "As vias reais para o financiamento na fase de aquisição." },
      { slug: "bridge-loans-vs-bank-construction-loans", t: "Crédito ponte vs crédito à construção", d: "O que cada instrumento faz, e o lugar de cada um." },
      { slug: "why-banks-finance-later", t: "Porque é que os bancos financiam mais tarde", d: "Os marcos que os bancos esperam — e o que preenche a lacuna." },
    ],
    faqH: "Perguntas dos promotores",
    faqs: [
      { q: "O que é o financiamento ao desenvolvimento em Portugal?", a: "Crédito de curto prazo, com garantia sénior, que financia aquisição, licenciamento e pré-vendas — as fases antes do crédito bancário à construção — reembolsado por refinanciamento bancário, venda ou pré-vendas." },
      { q: "O que pode ser financiado?", a: "Aquisição de terreno, licenciamento e custos de projeto, taxas municipais e o encargo até às pré-vendas — tipicamente €1–5m por operação para promotores residenciais experientes." },
      { q: "Quão rápida é a resposta?", a: "Condições indicativas em 24 horas após a descrição do projeto; o financiamento segue-se à subscrição, avaliação e due diligence." },
      { q: "Que garantia é exigida?", a: "Uma hipoteca de primeiro grau sobre o terreno ou o projeto, com o capital próprio do promotor investido e a Verde Capital a coinvestir." },
      { q: "Como é reembolsado?", a: "Pelo próximo evento de capital: refinanciamento bancário à construção, venda do ativo concluído ou parcialmente concluído, ou programas de pré-venda." },
      { q: "Quem pode candidatar-se?", a: "Promotores experientes com projetos residenciais em Portugal e capital próprio no negócio. Intermediários e consultores também podem apresentar negócios em nome dos promotores." },
    ],
    ctaH: "Comece pelo seu projeto.",
    ctaCopy: "Descreva-o abaixo. A Verde pergunta o que falta e constrói o seu processo — condições indicativas em 24 horas.",
    ctaBtn: "Obter condições indicativas",
    alt: "Estaleiro de desenvolvimento com grua na costa portuguesa",
  },
  nl: {
    eyebrow: "Ontwikkelfinanciering · Portugal",
    h1a: "Ontwikkelfinanciering.",
    h1b: "Zonder de bankbureaucratie.",
    heroCopy: "Kortlopende, senior gedekte financiering van €1–5 mln voor ervaren vastgoedontwikkelaars in Portugal — van grondverwerving en vergunningen tot bouwgereedheid, vóór conventioneel bankkrediet beschikbaar is.",
    answerH: "Wat is ontwikkelfinanciering in Portugal?",
    answerP: "Ontwikkelfinanciering is kortlopend, gedekt krediet dat een project financiert in de fasen die een conventionele bank doorgaans niet financiert: de grondaankoop, de gemeentelijke vergunningen en de voorverkoop die het project bankwaardig maakt. In Portugal stappen banken meestal in bij de bouwfase — ontwikkelfinanciering draagt het project tot die tijd, met een eerste rangs hypotheek als zekerheid, en wordt afgelost via bankherfinanciering, verkoop of voorverkoop.",
    forYouH: "Gemaakt voor ervaren ontwikkelaars",
    forYou: [
      "Grond met ontwikkelingspotentieel verwerven",
      "Vergunningen, ontwerp en gemeentelijke heffingen financieren",
      "Overbruggen naar voorverkoop en bouwkrediet",
      "Residentiële projecten in heel Portugal",
    ],
    offerH: "Wat Verde Capital biedt",
    offer: [
      { icon: PiggyBank, t: "Transacties van €1–5 mln", d: "Kortlopende, senior gedekte ontwikkelleningen voor ervaren woningbouwontwikkelaars." },
      { icon: Clock, t: "Looptijden van 6–24 maanden", d: "Duidelijke aflossingsroutes: bankherfinanciering, verkoop of voorverkoop." },
      { icon: Zap, t: "Indicatieve voorwaarden in 24 uur", d: "Beschrijf het project één keer; Verde, onze AI-underwriter, bouwt het dossier." },
      { icon: ShieldCheck, t: "Eerste rangs hypotheek", d: "Zekerheid op grond of project — met Verde die eigen kapitaal inbrengt." },
    ],
    howH: "Hoe het werkt",
    how: [
      { n: "1", t: "Beschrijf uw project", d: "In de online aanvraag — met de optie om plannen en taxaties toe te voegen." },
      { n: "2", t: "Verde bouwt het dossier", d: "AI-ondersteunde beoordeling met intern kredietteam en controle door lokale experts." },
      { n: "3", t: "Indicatieve voorwaarden in 24 uur", d: "Daarna due diligence, zekerheden en financiering — met een heldere route naar bankherfinanciering." },
    ],
    vsH: "Vergelijking met bankfinanciering",
    vsTable: { head: ["", "Verde financiering", "Conventioneel bankkrediet"], rows: [
      ["Projectfase", "Verwerving, vergunningen, voorbouwfase", "Bouw en oplevering, zodra mijlpalen er zijn"],
      ["Beslishheid", "Indicatieve voorwaarden in 24 uur", "Comitécycli na compleet dossier"],
      ["Typische omvang", "€1–5 mln", "Grotere transacties in de bouwfase"],
      ["Looptijd", "6–24 maanden", "Bouwfase plus verkoop"],
      ["Zekerheid", "Eerste rangs hypotheek, met Verde-medebelegging", "Hypotheek + cessies en voorverkoopdekking"],
    ] },
    guidesEyebrow: "Blijf leren",
    guides: [
      { slug: "development-finance-portugal-guide", t: "Complete gids vastgoedontwikkelfinanciering in Portugal", d: "Fasen, zekerheden, beoordeling, exits en risico's — de complete referentie." },
      { slug: "finance-land-before-planning", t: "Hoe financier je grond vóór de vergunning", d: "De echte routes naar financiering in de verwervingsfase." },
      { slug: "bridge-loans-vs-bank-construction-loans", t: "Bruglening vs bankbouwlening", d: "Wat elk instrument doet, en waar elk thuishoort." },
      { slug: "why-banks-finance-later", t: "Waarom banken later financieren", d: "De mijlpalen waar banken op wachten — en wat de kloof vult." },
    ],
    faqH: "Vragen van ontwikkelaars",
    faqs: [
      { q: "Wat is ontwikkelfinanciering in Portugal?", a: "Kortlopend, senior gedekt krediet dat verwerving, vergunningen en voorverkoop financiert — de fasen vóór bankbouwkrediet — afgelost via bankherfinanciering, verkoop of voorverkoop." },
      { q: "Wat kan worden gefinancierd?", a: "Grondverwerving, vergunningen en projectkosten, gemeentelijke heffingen en het recht tot voorverkoop — doorgaans €1–5 mln per transactie voor ervaren woningbouwontwikkelaars." },
      { q: "Hoe snel krijg je antwoord?", a: "Indicatieve voorwaarden binnen 24 uur na de projectomschrijving; financiering volgt na beoordeling, taxatie en due diligence." },
      { q: "Welke zekerheid is vereist?", a: "Een eerste rangs hypotheek op grond of project, met eigen kapitaal van de ontwikkelaar en Verde Capital als medebelegger." },
      { q: "Hoe wordt afgelost?", a: "Via de eerstvolgende kapitaalgebeurtenis: bankherfinanciering van de bouw, verkoop van het voltooide of gedeeltelijk voltooide actief, of voorverkoopprogramma's." },
      { q: "Wie kan aanvragen?", a: "Ervaren ontwikkelaars met residentiële projecten in Portugal en eigen kapitaal in de deal. Tussenpersonen en adviseurs kunnen ook namens ontwikkelaars indienen." },
    ],
    ctaH: "Begin bij uw project.",
    ctaCopy: "Beschrijf het hieronder. Verde vraagt wat ontbreekt en bouwt uw dossier — indicatieve voorwaarden in 24 uur.",
    ctaBtn: "Indicatieve voorwaarden aanvragen",
    alt: "Bouwlocatie met kraan aan de Portugese kust",
    guideLink: "Lees de complete gids",
  },
};

export default function DevelopmentFinance() {
  const { lang, lp } = useLang();
  const c = COPY[lang];
  const url = `${SITE}${lp("/development-finance-portugal")}`;
  return (
    <div className="bg-cream">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: c.faqs.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}${lp("/")}` },
          { "@type": "ListItem", position: 2, name: c.eyebrow, item: url },
        ],
      }} />

      {/* HERO */}
      <section className="pt-32 sm:pt-40 pb-16">
        <div className="container-x max-w-4xl">
          <Eyebrow>{c.eyebrow}</Eyebrow>
          <h1 className="display mt-6 text-[2.5rem] sm:text-5xl lg:text-[56px] text-charcoal" data-testid="dfp-headline">
            <MaskLineView>{c.h1a}</MaskLineView>
            <MaskLineView delay={0.12}><span className="text-verde-700">{c.h1b}</span></MaskLineView>
          </h1>
          <Reveal delay={0.25} className="mt-7 max-w-2xl">
            <p className="text-lg leading-relaxed text-ink">{c.heroCopy}</p>
          </Reveal>
          <Reveal delay={0.35} className="mt-9">
            <ArrowCta to={lp("/apply")} testid="dfp-cta">{c.ctaBtn}</ArrowCta>
          </Reveal>
        </div>
      </section>

      {/* ANSWER BOX */}
      <section className="pb-16">
        <div className="container-x max-w-4xl">
          <Reveal>
            <div className="rounded-2xl border border-sand bg-white p-8" data-testid="dfp-answer">
              <h2 className="font-display text-2xl font-bold text-charcoal">{c.answerH}</h2>
              <p className="mt-4 text-[15px] leading-relaxed text-ink">{c.answerP}</p>
              <Link to={lp("/insights/development-finance-portugal-guide")} data-testid="dfp-guide-link" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-verde-800 underline decoration-verde-800/30 underline-offset-4 hover:decoration-verde-800">
                {lang === "pt" ? "Ler o guia completo" : lang === "nl" ? c.guideLink : "Read the complete guide"} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OFFER CARDS */}
      <section className="pb-16">
        <div className="container-x">
          <Eyebrow>{c.offerH}</Eyebrow>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {c.offer.map((o, i) => (
              <Reveal key={o.t} delay={0.06 + i * 0.06}>
                <div className="card h-full p-6" data-testid={`dfp-offer-${i}`}>
                  <span className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] ring-1 ring-verde-800/10 shadow-[0_12px_26px_-16px_rgba(7,39,29,0.45)]">
                    <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                    <o.icon className="relative h-5 w-5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-display text-lg font-bold text-charcoal">{o.t}</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-ink">{o.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOR YOU + HOW IT WORKS */}
      <section className="pb-16">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-10">
          <Reveal>
            <div className="h-full rounded-2xl bg-verde-950 p-8 text-cream grain" data-testid="dfp-foryou">
              <h2 className="font-display text-2xl font-bold text-white">{c.forYouH}</h2>
              <ul className="mt-6 space-y-3.5">
                {c.forYou.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-cream/85">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-mint">
                      <Check className="h-2.5 w-2.5 text-verde-950" strokeWidth={3.5} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-sand bg-white p-8" data-testid="dfp-how">
              <h2 className="font-display text-2xl font-bold text-charcoal">{c.howH}</h2>
              <ol className="mt-6 space-y-5">
                {c.how.map((h) => (
                  <li key={h.n} className="flex items-start gap-4">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-verde-800 font-display text-[13px] font-bold text-white">{h.n}</span>
                    <div>
                      <p className="font-display text-[15px] font-bold text-charcoal">{h.t}</p>
                      <p className="mt-1 text-[13px] leading-relaxed text-fog">{h.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>
        </div>
      </section>

      {/* VS TABLE */}
      <section className="pb-16">
        <div className="container-x max-w-4xl">
          <h2 className="display text-3xl text-charcoal" data-testid="dfp-vs-heading">{c.vsH}</h2>
          <Reveal delay={0.1} className="mt-6">
            <div className="overflow-x-auto rounded-2xl border border-sand bg-white" data-testid="dfp-vs-table">
              <table className="w-full min-w-[560px] text-left text-[13px]">
                <thead>
                  <tr className="border-b border-sand bg-cream/60">
                    {c.vsTable.head.map((h) => <th key={h} className="px-5 py-3.5 font-display text-xs font-bold text-charcoal">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {c.vsTable.rows.map((row, r) => (
                    <tr key={r} className="border-b border-sand/60 last:border-0">
                      {row.map((cell, ci) => <td key={ci} className={`px-5 py-3.5 ${ci === 0 ? "font-semibold text-charcoal" : "text-ink"}`}>{cell}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* GUIDES */}
      <section className="pb-16">
        <div className="container-x">
          <Eyebrow>{c.guidesEyebrow}</Eyebrow>
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {c.guides.map((g, i) => (
              <Reveal key={g.slug} delay={0.06 + i * 0.06}>
                <Link
                  to={lp(`/insights/${g.slug}`)}
                  data-testid={`dfp-guide-${i}`}
                  className="card flex h-full flex-col p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-verde-950/10"
                >
                  <FileText className="h-5 w-5 text-verde-700" aria-hidden="true" />
                  <h3 className="mt-3 font-display text-[15px] font-bold leading-snug text-charcoal">{g.t}</h3>
                  <p className="mt-2 flex-1 text-[12.5px] leading-relaxed text-fog">{g.d}</p>
                  <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-verde-800">
                    {lang === "pt" ? "Ler" : "Read"} <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-16">
        <div className="container-x max-w-3xl">
          <h2 className="display text-3xl text-charcoal" data-testid="dfp-faq-heading">{c.faqH}</h2>
          <Reveal delay={0.1} className="mt-8">
            <Faq items={c.faqs} />
          </Reveal>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="pb-20">
        <div className="container-x">
          <Reveal>
            <div className="rounded-2xl bg-verde-950 px-8 py-14 text-center lg:py-20" data-testid="dfp-cta-band">
              <h2 className="display mx-auto max-w-2xl text-3xl sm:text-4xl text-cream">
                <MaskLineView>{c.ctaH}</MaskLineView>
              </h2>
              <p className="mx-auto mt-5 max-w-xl text-sm lg:text-base text-cream/75">{c.ctaCopy}</p>
              <div className="mt-9 flex justify-center">
                <ArrowCta to={lp("/apply")} variant="light" testid="dfp-cta-band">{c.ctaBtn}</ArrowCta>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
